/**
 * Strategy Review — step 2 of the BV associate's execution-strategy flow.
 *
 * Read-only preview of the COMPUTED execution strategy for the saved package
 * configuration: the exact date math the generated plan will use (backward /
 * forward / split / deferred passes), grouped for scanning, with feasibility
 * warnings. No inputs here — Generate persists the plan; each package's
 * installment is set afterwards via its Breakdown editor on the plan board
 * (the line-item total becomes the installment amount).
 */
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AlertTriangle, ArrowLeft, Factory, KeyRound, Lock, Truck, Zap } from "lucide-react";
import { generateExecutionPlan, getStrategyPreview } from "../../services/interiorExecution";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const GROUPS = [
  { key: "BEFORE_WBS", title: "Before builder milestones", hint: "must finish before the anchored WBS stage" },
  { key: "AFTER_WBS", title: "After builder milestones", hint: "start once the anchored WBS stage is reached" },
  { key: "POST_POSSESSION", title: "Post-possession", hint: "prep (procure/manufacture) before keys, install after" },
  { key: "DEFERRED", title: "Deferred", hint: "parked until possession — scheduled on activation" },
];

const URGENCY_STYLE = {
  OPEN: "bg-emerald-50 text-emerald-700 border-emerald-200",
  LOCK_IMMINENT: "bg-amber-50 text-amber-700 border-amber-200",
  LOCK_PASSED: "bg-rose-50 text-rose-600 border-rose-200",
};

const StrategyReview = ({ leadId, isRegenerate = false, onBack, onGenerated }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getStrategyPreview(leadId);
        if (!cancelled) setPreview(res);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Failed to compute the strategy");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leadId]);

  const submitGenerate = async () => {
    // Installment amounts are no longer entered here — they are set per package via
    // the Breakdown editor on the plan board (the line-item total IS the installment).
    const stages = (preview?.stages || []).map((s) => ({ stageKey: s.packageKey }));
    setBusy(true);
    try {
      const plan = await generateExecutionPlan(leadId, { stages, notes: notes || null }, isRegenerate);
      toast.success(isRegenerate ? "Execution plan regenerated" : "Execution plan generated");
      if (onGenerated) onGenerated(plan);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate plan");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2 p-1">
        {[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-2xl bg-slate-100 animate-pulse" />)}
      </div>
    );
  }
  if (!preview) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-slate-400">No strategy could be computed — configure packages first.</p>
        <button onClick={onBack} className="mt-3 text-sm text-flamingo-600 hover:underline">← Back to configuration</button>
      </div>
    );
  }

  const stagesByGroup = GROUPS.map((g) => ({
    ...g,
    stages: (preview.stages || []).filter((s) => s.group === g.key),
  })).filter((g) => g.stages.length > 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700">
          <ArrowLeft size={13} /> Edit configuration
        </button>
        <p className="text-xs text-slate-500 inline-flex items-center gap-1.5">
          <KeyRound size={12} />
          Expected possession: <span className="font-semibold text-slate-700">{fmt(preview.expectedPossessionDate)}</span>
        </p>
      </div>

      {(preview.warnings || []).length > 0 && (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1">
          {preview.warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-700 inline-flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
              {w.message || `${w.code}${w.packageKey ? ` — ${w.packageKey}` : ""}`}
            </p>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {stagesByGroup.map((group) => (
          <div key={group.key}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              {group.title} <span className="font-normal normal-case text-slate-400">— {group.hint}</span>
            </p>
            <div className="space-y-1.5">
              {group.stages.map((s) => (
                <div key={s.packageKey}
                  className={`rounded-xl border bg-white px-3.5 py-2.5 ${s.feasible ? "border-slate-200" : "border-rose-300 bg-rose-50/40"}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900">{s.label || s.packageKey}</p>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-slate-100 text-slate-500">{s.complexity}</span>
                    {s.durationDaysUsed != null && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500">
                        {s.durationDaysUsed}d{s.manufacturingDaysUsed ? ` + ${s.manufacturingDaysUsed}d mfg` : ""}
                      </span>
                    )}
                    {(s.startAfterLabel || s.finishBeforeLabel) && (
                      <span className="text-[11px] text-slate-400">
                        {s.startAfterLabel && (
                          <>after {s.startAfterLabel}{s.startAfterTargetDate ? ` (${fmt(s.startAfterTargetDate)})` : ""}</>
                        )}
                        {s.startAfterLabel && s.finishBeforeLabel && " · "}
                        {s.finishBeforeLabel && (
                          <>before {s.finishBeforeLabel}{s.finishBeforeTargetDate ? ` (${fmt(s.finishBeforeTargetDate)})` : ""}</>
                        )}
                      </span>
                    )}
                    {!s.feasible && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-rose-50 text-rose-600 border-rose-200">
                        <AlertTriangle size={11} /> Can't finish in time — consider Deferred
                      </span>
                    )}
                    {s.urgency && s.feasible && (
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${URGENCY_STYLE[s.urgency]}`}>
                        {s.urgency.replace("_", " ")}
                      </span>
                    )}
                    {/* Installment is set later via the per-package Breakdown editor.
                        Surface the BOQ material cost here as a planning hint only. */}
                    {s.boqMaterialCost != null && (
                      <span className="ml-auto text-[11px] text-slate-400">BOQ materials ₹{Number(s.boqMaterialCost).toLocaleString("en-IN")}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1.5 text-[11px] text-slate-500 flex-wrap">
                    {s.designLockDate && (
                      <span className="inline-flex items-center gap-1"><Lock size={11} /> Design lock {fmt(s.designLockDate)}</span>
                    )}
                    {s.procurementStartDate && (
                      <span className="inline-flex items-center gap-1"><Truck size={11} /> Procure {fmt(s.procurementStartDate)}</span>
                    )}
                    {s.manufacturingStartDate && (
                      <span className="inline-flex items-center gap-1">
                        <Factory size={11} /> Mfg {fmt(s.manufacturingStartDate)} → {fmt(s.manufacturingFinishDate)}
                      </span>
                    )}
                    {s.isPostPossession && s.installationStartDate ? (
                      <span>Install {fmt(s.installationStartDate)} → {fmt(s.installationFinishDate)}</span>
                    ) : s.executionStartDate ? (
                      <span>Execute {fmt(s.executionStartDate)} → {fmt(s.executionFinishDate)}</span>
                    ) : s.deferredUntil ? (
                      <span>Deferred until {fmt(s.deferredUntil)}</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 mt-4">
        <input
          value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)"
          className="flex-1 rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40"
        />
        <button
          onClick={submitGenerate} disabled={busy}
          className="inline-flex items-center gap-2 rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 whitespace-nowrap"
        >
          <Zap size={15} /> {busy ? "Generating…" : isRegenerate ? "Regenerate Plan" : "Generate Plan"}
        </button>
      </div>
      {isRegenerate && (
        <p className="text-[11px] text-amber-600 mt-1.5 text-right">Regenerating supersedes the current plan (history kept).</p>
      )}
    </div>
  );
};

export default StrategyReview;
